import { forwardRef, useCallback, useMemo, useState, ChangeEvent, CSSProperties } from "react";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useId } from "../../hooks/useId";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { useValidity, useInputDisabled, useInputReadonly, useSubmitting, Shared } from "capitalrx-components-2.0/lib/forms";
import cn from "classnames";
import { debounce } from "lodash";

import { SearchTextboxProps } from "./SearchTextbox";
import { Placeholder } from "./Placeholder";
import { Overlay } from "./Overlay";

import TextboxStyles from "capitalrx-components-2.0/lib/forms/Textbox/styles.module.scss";
import dropDownMenuStyles from "capitalrx-components-2.0/lib/forms/Dropdown/components/Menu/styles.module.scss";
import dropDownMenuListStyles from "capitalrx-components-2.0/lib/forms/Dropdown/components/Option/styles.module.scss";

import styles from "./styles.module.scss";

/**
 * The `Input` component properties.
 */
export interface InputProps extends SearchTextboxProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * The input type.
   */
  readonly type?: "text" | "password";

  /**
   * List item click function
   */
  readonly onMenuItemClick?: (data: string | number) => void;

  /**
   * the function that will be fetching the data. used to trigger the api call
   */
  readonly fetchData?: (data: string) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    type = "text",
    label,
    tooltip,
    instructions,
    hideInstructions,
    hideLabel,
    prefix,
    suffix,
    defaultValue = null,
    required = false,
    autoFocus,
    placeholder,
    maxLength,
    tabIndex,
    onChange,
    onFocusEnter,
    onFocusExit,
    searchResults = [],
    onMenuItemClick,
    fetchData,
  } = props;

  const { value = defaultValue } = props;
  const [openListContainer, setOpenListContainer] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [TextboxStyles.Textbox, styles.ContainerContent, "textbox"],
    testIdPrefix: "textbox",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const [prefixSize, setPrefixSize] = useState<number | null>(null);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);

  const handleCloseItemContainer = () => {
    setOpenListContainer(false);
  };

  const handleOpenItemContainer = () => {
    setOpenListContainer(true);
  };

  const debouncedHandleOnChange = useCallback(
    debounce((value) => {
      if (typeof fetchData !== "undefined" && searchTerm?.length) {
        fetchData(value);
        handleOpenItemContainer();
      }

      if (searchTerm === null || searchTerm === "") {
        setOpenListContainer(false);
      }
    }, 500),
    [fetchData, searchTerm],
  );

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(ev.target.value, value);
      setSearchTerm(ev.target.value);

      if (ev.target.value.length >= 3) {
        debouncedHandleOnChange(ev.target.value);
      } else {
        handleCloseItemContainer();
      }
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
    handleCloseItemContainer();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
    handleCloseItemContainer();
  }, [onFocusExit]);

  const overlayLayoutHandler = useCallback((prefixSize: number | null, suffixSize: number | null) => {
    setPrefixSize(prefixSize);
    setSuffixSize(suffixSize);
  }, []);

  const style = useMemo(() => {
    if (prefixSize === null && suffixSize === null) return {};
    const result: CSSProperties = {};
    if (prefixSize !== null) result.paddingLeft = `${prefixSize}px`;
    if (suffixSize !== null) result.paddingRight = `${suffixSize}px`;
    return result;
  }, [prefixSize, suffixSize]);

  const dropdownMenuStyles = cn(dropDownMenuStyles.Menu, styles.DropDownListContainer);

  const dropdownMenuListStyles = cn(dropDownMenuListStyles.Content);

  return (
    <div className={styles.ContainerWrapper}>
      <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} />}>
        <Shared.InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={id}
          label={label}
          instructions={instructions}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <Body className="input-container">
            <input
              id={id}
              type={type}
              aria-labelledby={`${id}-label`}
              aria-invalid={invalid}
              aria-required={required}
              className="input"
              disabled={disabled}
              autoFocus={autoFocus}
              readOnly={readOnly || submitting}
              ref={ref}
              maxLength={maxLength}
              tabIndex={disabled || readOnly || submitting ? -1 : tabIndex ?? 0}
              placeholder={placeholder}
              style={style}
              value={maxLength !== undefined ? (value ?? "").substring(0, maxLength) : value ?? ""}
              onChange={changeHandler}
              onFocus={focusHandler}
              onBlur={() => setTimeout(blurHandler, 300)}
              autoComplete="off"
            />
            <Overlay prefix={prefix} suffix={suffix} onLayout={overlayLayoutHandler} />
          </Body>
          {openListContainer && searchTerm && (
            <div className={dropdownMenuStyles}>
              <ul>
                {searchResults.length ? (
                  searchResults.map((data) => {
                    return (
                      <li
                        key={data.label}
                        className={dropdownMenuListStyles}
                        onClick={() => {
                          onMenuItemClick && onMenuItemClick(data);
                          handleCloseItemContainer();
                          setSearchTerm(data.value);
                        }}
                      >
                        {data.label}
                      </li>
                    );
                  })
                ) : (
                  <li onClick={handleCloseItemContainer} className={dropdownMenuListStyles}>
                    No results found.
                  </li>
                )}
              </ul>
            </div>
          )}
        </Shared.InputContainer>
      </LoadingGroup.Placeholder>
    </div>
  );
});
